import { isSampleTestRow } from "./sampleDataFilter";
import { ExcelExportColumn } from "../types";
import {
  RetailItemExcelHeader,
  RetailItemExcelSourceField,
} from "../enums/retailItemExcel";
import { retailItemExcelColumn } from "../enums/typedExcel";
import { ExcelEmptyDefault } from "../constants/defaults";

/** Excel headers/source fields for Retail Items Setup (Spa / Golf). */
export const retailItemExcelColumns: ExcelExportColumn[] = [
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemNumber,
    RetailItemExcelSourceField.ItemNumber,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemDescription,
    RetailItemExcelSourceField.ItemDescription,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Category,
    RetailItemExcelSourceField.Category,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SubCategory1,
    RetailItemExcelSourceField.SubCategory1,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SubCategory2,
    RetailItemExcelSourceField.SubCategory2,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SubCategory3,
    RetailItemExcelSourceField.SubCategory3,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SubCategory4,
    RetailItemExcelSourceField.SubCategory4,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SubCategory5,
    RetailItemExcelSourceField.SubCategory5,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.UnitOfMeasure,
    RetailItemExcelSourceField.UnitOfMeasure,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SalesPrice,
    RetailItemExcelSourceField.SalesPrice,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.BarCode,
    RetailItemExcelSourceField.ItemNumber,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.SecondaryBarCode,
    RetailItemExcelSourceField.SecondaryBarCode,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Tax,
    RetailItemExcelSourceField.Tax,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemType,
    RetailItemExcelSourceField.ItemType,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.CommissionPercent,
    RetailItemExcelSourceField.CommissionPercent,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.CommissionableItem,
    RetailItemExcelSourceField.CommissionableItem,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.CommissionRequired,
    RetailItemExcelSourceField.CommissionRequired,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.UseInventory,
    RetailItemExcelSourceField.UseInventory,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.GroupingKey,
    RetailItemExcelSourceField.GroupingKey,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.OpenItems,
    RetailItemExcelSourceField.OpenItems,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Multipacks,
    RetailItemExcelSourceField.Multipacks,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Gratuity,
    RetailItemExcelSourceField.Gratuity,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ServiceCharge,
    RetailItemExcelSourceField.ServiceCharge,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.OutletCode,
    RetailItemExcelSourceField.OutletCode,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.Quantity,
    RetailItemExcelSourceField.Quantity,
    ExcelEmptyDefault.Empty,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.UnitCost,
    RetailItemExcelSourceField.UnitCost,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.ItemPar,
    RetailItemExcelSourceField.ItemPar,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.OutletSwitch,
    RetailItemExcelSourceField.OutletSwitch,
    0,
  ),
  retailItemExcelColumn(
    RetailItemExcelHeader.AllowEarn,
    RetailItemExcelSourceField.AllowEarn,
    0,
  ),
];

/** Normalize onboarding retail-item rows for Excel export. */
export const mapRetailItemRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  if (!Array.isArray(sheetData)) {
    return [];
  }

  return sheetData
    .filter(
      (row) =>
        row &&
        typeof row === "object" &&
        !isSampleTestRow(row as Record<string, unknown>),
    )
    .map((row) => {
      const record = row as Record<string, unknown>;
      const itemNumber = record[RetailItemExcelSourceField.ItemNumber];
      const outletCode =
        record[RetailItemExcelSourceField.OutletCode] ??
        record[RetailItemExcelSourceField.Outlet] ??
        record[RetailItemExcelSourceField.OutletName] ??
        "";
      const barCode = record[RetailItemExcelSourceField.BarCode];

      return {
        ...record,
        [RetailItemExcelHeader.OutletCode]: outletCode,
        [RetailItemExcelHeader.BarCode]:
          barCode !== undefined &&
          barCode !== null &&
          String(barCode).trim() !== ""
            ? barCode
            : (itemNumber ?? ""),
        [RetailItemExcelHeader.SubCategory1]:
          record[RetailItemExcelSourceField.SubCategory1] ?? "",
        [RetailItemExcelHeader.SubCategory2]:
          record[RetailItemExcelSourceField.SubCategory2] ?? "",
        [RetailItemExcelHeader.SubCategory3]:
          record[RetailItemExcelSourceField.SubCategory3] ?? "",
        [RetailItemExcelHeader.SubCategory4]:
          record[RetailItemExcelSourceField.SubCategory4] ?? "",
        [RetailItemExcelHeader.SubCategory5]:
          record[RetailItemExcelSourceField.SubCategory5] ?? "",
        [RetailItemExcelHeader.UnitOfMeasure]:
          record[RetailItemExcelSourceField.UnitOfMeasure] ?? "",
        [RetailItemExcelHeader.SecondaryBarCode]:
          record[RetailItemExcelSourceField.SecondaryBarCode] ?? "",
        [RetailItemExcelHeader.Tax]:
          record[RetailItemExcelSourceField.Tax] ?? "",
        [RetailItemExcelHeader.CommissionPercent]:
          record[RetailItemExcelSourceField.CommissionPercent] ?? 0,
        [RetailItemExcelHeader.CommissionableItem]:
          record[RetailItemExcelSourceField.CommissionableItem] ?? 0,
        [RetailItemExcelHeader.CommissionRequired]:
          record[RetailItemExcelSourceField.CommissionRequired] ?? 0,
        [RetailItemExcelHeader.UseInventory]:
          record[RetailItemExcelSourceField.UseInventory] ?? 0,
        [RetailItemExcelHeader.GroupingKey]:
          record[RetailItemExcelSourceField.GroupingKey] ?? 0,
        [RetailItemExcelHeader.OpenItems]:
          record[RetailItemExcelSourceField.OpenItems] ?? 0,
        [RetailItemExcelHeader.Multipacks]:
          record[RetailItemExcelSourceField.Multipacks] ?? 0,
        [RetailItemExcelHeader.Gratuity]:
          record[RetailItemExcelSourceField.Gratuity] ?? 0,
        [RetailItemExcelHeader.ServiceCharge]:
          record[RetailItemExcelSourceField.ServiceCharge] ?? 0,
        [RetailItemExcelHeader.Quantity]:
          record[RetailItemExcelSourceField.Quantity] ?? "",
        [RetailItemExcelHeader.UnitCost]:
          record[RetailItemExcelSourceField.UnitCost] ?? 0,
        [RetailItemExcelHeader.ItemPar]:
          record[RetailItemExcelSourceField.ItemPar] ?? 0,
        [RetailItemExcelHeader.OutletSwitch]:
          record[RetailItemExcelSourceField.OutletSwitch] ?? 0,
        [RetailItemExcelHeader.AllowEarn]:
          record[RetailItemExcelSourceField.AllowEarn] ?? 0,
      };
    });
};
